// Logging in mid-flow and coming back (Phase D, Signup/docs/28).
//
// "Log in" goes to the classic login with page=submission-{step}; login= pre-fills the form
// (email or Workbook ID, which is the client id). Once the actor is in, the login sends them
// to /submission/flow/continue with the casting call and k, their session key. The continue
// page trades k for the flow's token and opens the step asked for, or the furthest one the
// submission has reached when that comes first. The win-back emails use the same link.

// @ts-ignore
import qs from "query-string";
import { ISubmissionDraftDto } from "../../../shared/api/submission/types";

export const SUBMISSION_PAGE_PREFIX = "submission-";
// Signed in means the account exists, so step 1 is behind them.
export const FIRST_SIGNED_IN_STEP = 2;
export const LAST_STEP = 4;

// What a flow URL takes through the login. The login forwards these three and nothing else.
const KEPT_PARAMS = ["bd", "roleid", "src"];
// What the hand-off leaves behind once it's done: the session key never stays in the address
// bar or the history.
// eref (an email's signed send row) is reported by the continue page, then dropped too (emailReturn.ts).
const HAND_OFF_PARAMS = ["k", "page", "login", "eref"];

const clampStep = (step: number): number =>
  Math.min(LAST_STEP, Math.max(FIRST_SIGNED_IN_STEP, step));

const parse = (search?: string): any => {
  try {
    return qs.parse(search || "") || {};
  } catch (e) {
    return {};
  }
};

// The classic login, set to bring the actor back to `step`. Without a step it brings them as
// far as their submission got.
export const flowLoginUrl = (
  search: string | undefined,
  step: number = LAST_STEP,
  login?: string | number | null
): string => {
  const current = parse(search);
  const params: any = {};
  KEPT_PARAMS.forEach((key) => {
    if (current[key]) params[key] = current[key];
  });
  if (login !== undefined && login !== null && String(login).trim() !== "") {
    params.login = String(login).trim();
  }
  params.page = `${SUBMISSION_PAGE_PREFIX}${clampStep(step)}`;
  return `${process.env.REACT_APP_CWB_Actor_Login}?${qs.stringify(params)}`;
};

// page=submission-{step} → the step asked for (2 to 4), or null when there is none.
export const requestedStep = (search?: string): number | null => {
  const page = String(parse(search).page || "").trim().toLowerCase();
  if (!page.startsWith(SUBMISSION_PAGE_PREFIX)) return null;
  const step = Number(page.slice(SUBMISSION_PAGE_PREFIX.length));
  return Number.isInteger(step) && page.length > SUBMISSION_PAGE_PREFIX.length ? clampStep(step) : null;
};

// The session key the login handed over, if any.
export const handOffKey = (search?: string): string => {
  const k = parse(search).k;
  return typeof k === "string" ? k.trim() : "";
};

// The furthest step a submission has reached: step 3 needs a photo and the attestation (what
// step 2 requires), step 4 the phone.
export const furthestStep = (draft: ISubmissionDraftDto | null): number => {
  if (!draft || !draft.photo || !draft.ageAttested) return FIRST_SIGNED_IN_STEP;
  return draft.phoneVerified ? LAST_STEP : 3;
};

// Where a signed-in actor resumes. A sent submission has nothing left to do.
export const resumePath = (requested: number | null, draft: ISubmissionDraftDto | null): string => {
  if (draft && draft.status === "Sent") return "/submission/sent";
  const furthest = furthestStep(draft);
  return `/submission/flow/step/${requested ? Math.min(requested, furthest) : furthest}`;
};

// The search string without the hand-off's own params.
export const withoutHandOff = (search?: string, keep: string[] = []): string => {
  const params = parse(search);
  HAND_OFF_PARAMS.filter((key) => !keep.includes(key)).forEach((key) => delete params[key]);
  const out = qs.stringify(params);
  return out ? `?${out}` : "";
};
